import { Broadcast } from "@switcherstudio/switcher-api-client";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import {
    setSelectedYoutubeChannel,
    updateFacebookForm,
    updateSelectedStream,
    updateTwitchForm,
    updateYoutubeForm
} from "store/platforms/slice";
import {
    createFacebookStream,
    updateFacebookStream,
    createYouTubeEvent,
    updateYoutubeEvent,
    updateTwitchChannel,
    updateVideoPlayerStream
} from "store/platforms/thunks";
import {
    FacebookForm,
    FacebookProfile,
    TwitchForm,
    YouTubeForm
} from "store/platforms/types";
import { AppDispatch } from "store/store";
import {
    DestinationDescriptor,
    YouTubePrivacyOptions
} from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";

export enum DestinationProcessingStatus {
    Processing = "processing",
    Success = "success",
    Error = "error"
}

// Per-destination connection engine, extracted from the New Livestream wizard's Processing step (sc-56801).
export const useDestinationConnections = () => {
    const { general } = useSelector((state: RootState) => state.platforms);

    const dispatch = useDispatch<AppDispatch>();

    const { dispatchApiRequest: getStreamSetting } = useSwitcherClient(
        (client) => client.switcherStreamSettings_GetSwitcherStreamSetting,
        { suppressErrorBoundary: true }
    );

    const { dispatchApiRequest: updateStreamSetting } = useSwitcherClient(
        (client) => client.switcherStreamSettings_PostSwitcherStreamSetting,
        { suppressErrorBoundary: true }
    );

    const { dispatchApiRequest: createOutput } = useSwitcherClient(
        (client) => client.broadcasts_CreateOutput,
        { suppressErrorBoundary: true }
    );

    const [postStatuses, setPostStatuses] = useState<
        Record<string, DestinationProcessingStatus>
    >({});

    const [postErrors, setPostErrors] = useState<Record<string, string>>({});

    // small helpers for status + errors, shared by the initial submission
    // and by individual destination retries
    const setStatus = useCallback(
        (id: string, status: DestinationProcessingStatus) =>
            setPostStatuses((prev) => ({ ...prev, [id]: status })),
        []
    );

    const clearError = useCallback(
        (id: string) => setPostErrors((prev) => ({ ...prev, [id]: undefined })),
        []
    );

    const setErrorMsg = useCallback(
        (id: string, message: string) =>
            setPostErrors((prev) => ({ ...prev, [id]: message })),
        []
    );

    // run a unit of work with consistent status/error handling
    const withStatus = useCallback(
        (id: string, work: () => Promise<void>) => {
            setStatus(id, DestinationProcessingStatus.Processing);
            return (async () => {
                try {
                    await work();
                    clearError(id);
                    setStatus(id, DestinationProcessingStatus.Success);
                } catch (err: any) {
                    setErrorMsg(
                        id,
                        err?.message ?? String(err) ?? "Unknown error"
                    );
                    setStatus(id, DestinationProcessingStatus.Error);
                    throw err;
                }
            })();
        },
        [setStatus, clearError, setErrorMsg]
    );

    // Seeds each destination's platform form from platformSpecificSettings - must run before processDestination.
    const seedDestinationForms = useCallback(
        (destinations: DestinationDescriptor[]) => {
            for (const d of destinations) {
                const { id: destId, platform } = d;

                switch (platform) {
                    case PlatformId.Youtube: {
                        // Make selectedPrivacy the correct type (fallback to default if no selection)
                        const selectedPrivacy = d?.platformSpecificSettings
                            ?.selectedPrivacy
                            ? {
                                  name: d?.platformSpecificSettings?.selectedPrivacy.toString(),
                                  value: d?.platformSpecificSettings?.selectedPrivacy.toLowerCase()
                              }
                            : {
                                  name: YouTubePrivacyOptions.Public.toString(),
                                  value: YouTubePrivacyOptions.Public.toLowerCase()
                              };
                        const form: YouTubeForm = {
                            isForKids:
                                d?.platformSpecificSettings?.isForKids ?? false,
                            allowEmbedding:
                                d?.platformSpecificSettings?.allowEmbedding ??
                                false,
                            selectedPrivacy
                        };
                        dispatch(updateYoutubeForm(form));
                        dispatch(
                            setSelectedYoutubeChannel(
                                d?.platformSpecificSettings?.channel
                            )
                        );
                        break;
                    }
                    case PlatformId.Facebook: {
                        const form: FacebookForm = {
                            selectedTags:
                                d?.platformSpecificSettings?.contentTags ?? [],
                            enableBrandedContent:
                                !!d?.platformSpecificSettings
                                    ?.brandedContentSelection,
                            brandedContentSelection:
                                d?.platformSpecificSettings
                                    ?.brandedContentSelection ?? "",
                            enableCrossposting:
                                d?.platformSpecificSettings?.enableCrossposting,
                            selectedCrosspostingOptions:
                                d?.platformSpecificSettings?.crosspostOptions ??
                                [],
                            enableModeration:
                                d.platformSpecificSettings
                                    ?.commentModerationOptions?.length > 0,
                            selectedCommentModerationOptions:
                                d?.platformSpecificSettings
                                    ?.commentModerationOptions ?? [],
                            privacy: d?.platformSpecificSettings
                                ?.selectedVisibility || {
                                value: "EVERYONE",
                                name: "Everyone"
                            }
                        };
                        dispatch(updateFacebookForm({ [destId]: form }));
                        break;
                    }
                    case PlatformId.Twitch: {
                        const selectedGame = d?.platformSpecificSettings
                            ?.category
                            ? d?.platformSpecificSettings?.category?.id.toString()
                            : null;
                        const form: TwitchForm = {
                            delay: d?.platformSpecificSettings?.delay ?? 0,
                            selectedGame,
                            ingestServer:
                                d?.platformSpecificSettings?.ingestServer
                        };
                        dispatch(updateTwitchForm(form));
                        break;
                    }
                    case PlatformId.VideoPlayer:
                    case PlatformId.AmazonLive:
                    case PlatformId.Caast:
                    case PlatformId.Instafeed:
                    case PlatformId.MsStream:
                    case PlatformId.Rtmp:
                    default: {
                        // Ignore collections and Custom RTMP channels
                        break;
                    }
                }
            }
        },
        [dispatch]
    );

    /**
     * Runs the platform-specific work needed to connect a single destination
     * to `broadcast`. Used both for the initial batch submission and for a
     * later per-destination retry - it does not touch status/error state
     * itself (that's `withStatus`'s job) so both callers get identical
     * success/error handling.
     */
    const processDestination = useCallback(
        async (d: DestinationDescriptor, broadcast: Broadcast) => {
            const { platform } = d;

            switch (platform) {
                case PlatformId.Youtube: {
                    const action =
                        general.selectedStreams.youtube === null
                            ? createYouTubeEvent({
                                  showNotification: false,
                                  swiBroadcastId: broadcast.Id,
                                  isSimulcast: true
                              })
                            : updateYoutubeEvent({
                                  swiBroadcastId: broadcast.Id,
                                  isSimulcast: true
                              });

                    const res = await dispatch(action);
                    // Mitigate duplicate live events on retry: once the
                    // YouTube event exists, subsequent attempts should update
                    // it rather than create a new one.
                    if ((res as any)?.payload) {
                        dispatch(
                            updateSelectedStream({
                                id: "youtube",
                                stream: (res as any).payload
                            })
                        );
                    }
                    await createOutput([broadcast.Id, (res as any).payload]);
                    break;
                }
                case PlatformId.Facebook: {
                    const action =
                        general.selectedStreams.facebook === null
                            ? createFacebookStream({
                                  broadcastId: broadcast.Id,
                                  selectedDest: {
                                      id: d.id,
                                      edge: d.platformSpecificSettings?.edge,
                                      name: d.name,
                                      picture: {
                                          data: { url: null }
                                      }
                                  } as FacebookProfile,
                                  showNotification: false,
                                  isSimulcast: true
                              })
                            : updateFacebookStream({
                                  selectedDest: {
                                      id: d.id,
                                      edge: d.type,
                                      name: d.name
                                  } as FacebookProfile,
                                  isSimulcast: true
                              });

                    // .unwrap() so a rejected thunk (e.g. a blacklisted
                    // account's 402) actually throws and surfaces its message
                    // in this destination's error state instead of silently
                    // resolving with an error payload (sc-57766).
                    const result = await dispatch(action).unwrap();
                    // Same mitigation as YouTube above. Note: `selectedStreams.facebook`
                    // is tracked per-platform, not per-destination, so this doesn't
                    // fully hold up when there are two Facebook destinations (e.g. two
                    // pages) in the same submission - see report for details.
                    if (result) {
                        dispatch(
                            updateSelectedStream({
                                id: "facebook",
                                stream: result
                            })
                        );
                    }
                    await createOutput([broadcast.Id, result as any]);
                    break;
                }
                case PlatformId.Twitch: {
                    const res = await dispatch(
                        updateTwitchChannel({
                            broadcastId: broadcast.Id,
                            isSimulcast: true,
                            showNotification: false
                        })
                    );
                    await createOutput([broadcast.Id, (res as any).payload]);
                    break;
                }
                case PlatformId.VideoPlayer: {
                    await dispatch(
                        updateVideoPlayerStream({
                            broadcast,
                            isSimulcast: true,
                            showNotification: false
                        })
                    );
                    break;
                }

                case PlatformId.AmazonLive:
                case PlatformId.Caast:
                case PlatformId.Instafeed:
                case PlatformId.MsStream:
                case PlatformId.Rtmp: {
                    const existing = await getStreamSetting([d.id]);

                    // sanitize + update
                    const { ["switcher-stream-setting-id"]: _omit, ...clean } =
                        existing ?? {};
                    clean.broadcastId = broadcast.Id;
                    clean["is-direct-to-provider"] = false;
                    clean["user-editable"] = false;

                    const res = await updateStreamSetting([clean]);
                    await createOutput([broadcast.Id, res]);
                    break;
                }

                default: {
                    throw new Error(`Unsupported platform: ${platform}`);
                }
            }
        },
        [
            general?.selectedStreams,
            dispatch,
            createOutput,
            getStreamSetting,
            updateStreamSetting
        ]
    );

    // Retries connecting a single destination without re-running the rest of the batch.
    const retryDestination = useCallback(
        (destination: DestinationDescriptor, broadcast: Broadcast) => {
            return withStatus(destination.id, () =>
                processDestination(destination, broadcast)
            ).catch(() => {
                // Status/error state is already recorded by withStatus - this
                // catch only exists to avoid an unhandled promise rejection.
            });
        },
        [withStatus, processDestination]
    );

    return {
        postStatuses,
        postErrors,
        setStatus,
        clearError,
        setErrorMsg,
        withStatus,
        seedDestinationForms,
        processDestination,
        retryDestination
    };
};
